/**
 * Transfer wizard API (Angular: transfer-process.component.ts).
 */
import apiClient from "@/lib/api/apiClient"
import type {
  CompanyOptionRow,
  PaginatedResponse,
  PropertyOptionRow,
  PropertyStaffRow,
  ResidentRow,
  TransferProcessLogRow,
} from "../types"

export async function getTransferProcessLog(
  params: Record<string, string | number | undefined>,
): Promise<TransferProcessLogRow[]> {
  const res = await apiClient.get<TransferProcessLogRow[]>("/api/transfer_process_log", { params })
  const d = res.data
  return Array.isArray(d) ? d : []
}

export async function postTransferProcessLog(body: Record<string, unknown>): Promise<unknown> {
  const res = await apiClient.post("/api/transfer_process_log", body)
  return res.data
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === "object" && !Array.isArray(x)
}

/** True when `inner` looks like a property row (ids or typical columns Angular patches from GET). */
function propertyInnerLooksLikeRow(inner: Record<string, unknown>): boolean {
  return (
    inner.property_id != null ||
    inner.id != null ||
    inner.fk_property_id != null ||
    (inner.property_name != null &&
      (inner.fk_company_id != null ||
        (inner.company_name != null && String(inner.company_name).trim() !== ""))) ||
    inner.address1 != null ||
    inner.payee_name != null
  )
}

function companyInnerLooksLikeRow(inner: Record<string, unknown>): boolean {
  const cn =
    inner.company_name != null && String(inner.company_name).trim() !== ""
      ? String(inner.company_name).trim()
      : ""
  return (
    inner.company_id != null ||
    inner.id != null ||
    inner.fk_company_id != null ||
    (cn !== "" &&
      (inner.address1 != null ||
        inner.city != null ||
        inner.phone != null ||
        inner.email != null ||
        inner.fk_company_id != null)) ||
    inner.address1 != null ||
    inner.city != null ||
    inner.state != null ||
    inner.zip != null ||
    inner.country != null ||
    inner.payee_name != null
  )
}

/**
 * Unwrap `{ data | result | payload | property }` envelopes (including shallow nesting) so the wizard
 * uses a flat row like Angular’s `this.propertyData = Object.assign({}, response)`.
 */
function normalizePropertyDetailBody(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw) && raw.length > 0 && isRecord(raw[0])) {
    return normalizePropertyDetailBody(raw[0])
  }
  if (!isRecord(raw)) return {}
  let o: Record<string, unknown> = raw
  const envelopeKeys = ["data", "result", "payload", "property"] as const
  for (let depth = 0; depth < 4; depth++) {
    let advanced = false
    for (const k of envelopeKeys) {
      const inner = o[k]
      if (isRecord(inner) && propertyInnerLooksLikeRow(inner)) {
        o = inner
        advanced = true
        break
      }
    }
    if (!advanced) break
  }
  return o
}

function normalizeCompanyDetailBody(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw) && raw.length > 0 && isRecord(raw[0])) {
    return normalizeCompanyDetailBody(raw[0])
  }
  if (!isRecord(raw)) return {}
  let o: Record<string, unknown> = raw
  const envelopeKeys = ["data", "result", "payload", "company"] as const
  for (let depth = 0; depth < 4; depth++) {
    let advanced = false
    for (const k of envelopeKeys) {
      const inner = o[k]
      if (isRecord(inner) && companyInnerLooksLikeRow(inner)) {
        o = inner
        advanced = true
        break
      }
    }
    if (!advanced) break
  }
  return o
}

export async function getPropertyForTransfer(
  propertyId: string,
): Promise<Record<string, unknown>> {
  const res = await apiClient.get<unknown>(`/api/property/${propertyId}`, {
    params: { withSoftDeleted: true },
  })
  return normalizePropertyDetailBody(res.data)
}

export async function getCompanyForTransfer(companyId: string): Promise<Record<string, unknown>> {
  const res = await apiClient.get<unknown>(`/api/company/${companyId}`, {
    params: { withSoftDeleted: true },
  })
  return normalizeCompanyDetailBody(res.data)
}

export async function getCompanyById(companyId: string): Promise<Record<string, unknown>> {
  const res = await apiClient.get<Record<string, unknown>>(`/api/company/${companyId}`)
  return res.data ?? {}
}

export async function searchAllCompanies(
  q?: string,
  page?: "transfer_data",
): Promise<CompanyOptionRow[]> {
  const params: Record<string, string> = {}
  if (q) params.q = q
  if (page) params.page = page
  const res = await apiClient.get<CompanyOptionRow[]>("/api/all_company", { params })
  return Array.isArray(res.data) ? res.data : []
}

export async function searchAllProperties(
  companyId: string,
  q?: string,
): Promise<PropertyOptionRow[]> {
  const params: Record<string, string> = { company_id: String(companyId) }
  if (q) params.q = q
  const res = await apiClient.get<PropertyOptionRow[]>("/api/all_property", { params })
  return Array.isArray(res.data) ? res.data : []
}

/** `GET /api/property_staff` — pass through query params. Company *edit* may use `roles: "company"` (`company-edit.component.ts` `loadStaffs`); *transfer* uses `company_id` only (`transfer-process.component.ts`). */
export async function getPropertyStaff(
  params: Record<string, string | number | undefined>,
): Promise<PropertyStaffRow[]> {
  const res = await apiClient.get<PropertyStaffRow[]>("/api/property_staff", { params })
  return Array.isArray(res.data) ? res.data : []
}

export async function getAlnPropertyManagers(propertyId: string): Promise<unknown[]> {
  const res = await apiClient.get<unknown[]>(`/api/aln_property_managers/${propertyId}`)
  return Array.isArray(res.data) ? res.data : []
}

/** Laravel paginated JSON — tolerate `{ data: [...] }` or legacy shapes. */
function normalizeResidentPaginatedPayload(payload: unknown): PaginatedResponse<ResidentRow> {
  if (Array.isArray(payload)) {
    const rows = payload as ResidentRow[]
    return { data: rows, current: 1, last: 1, total: rows.length }
  }
  if (payload == null || typeof payload !== "object") {
    return { data: [], current: 1, last: 1, total: 0 }
  }
  const o = payload as Record<string, unknown>
  const rows = o.data
  if (Array.isArray(rows)) {
    return {
      data: rows as ResidentRow[],
      current: o.current != null ? Number(o.current) : 1,
      last: o.last != null ? Number(o.last) : 1,
      total: o.total != null ? Number(o.total) : rows.length,
    }
  }
  return { data: [], current: 1, last: 1, total: 0 }
}

/**
 * `GET /api/resident` — Angular `loadResidents` uses **camelCase** `propertyId` and `companyId` only when
 * `transfer_company` and a destination `fk_company_id` is set; otherwise it sends `propertyId` = route id
 * (same for company transfer before a destination is chosen). Do not send `companyId` for that case.
 * `getPendingApplications` uses snake_case `property_id` / `company_id` with the same branching.
 */
export async function getResidents(
  params: Record<string, string | number | undefined>,
): Promise<PaginatedResponse<ResidentRow>> {
  const res = await apiClient.get<unknown>("/api/resident", { params })
  return normalizeResidentPaginatedPayload(res.data)
}

export async function getPendingApplications(
  params: Record<string, string | number | undefined>,
): Promise<PaginatedResponse<ResidentRow>> {
  const res = await apiClient.get<unknown>("/api/pending_applications", {
    params,
  })
  return normalizeResidentPaginatedPayload(res.data)
}

export async function postTransferResidents(body: Record<string, unknown>): Promise<void> {
  await apiClient.post("/api/transfer_residents", body)
}

export async function postTransferPropertyToCompany(body: Record<string, unknown>): Promise<void> {
  await apiClient.post("/api/transfer_propertytocompany", body)
}

export async function postTransferCompany(body: Record<string, unknown>): Promise<void> {
  await apiClient.post("/api/transfer_company", body)
}

export async function postMergeProperty(body: Record<string, unknown>): Promise<void> {
  await apiClient.post("/api/merge_property", body)
}

export async function postSplitProperty(body: Record<string, unknown>): Promise<unknown> {
  const res = await apiClient.post("/api/split_property", body)
  return res.data
}

export async function postPropertyJson(propertyId: string, body: Record<string, unknown>): Promise<void> {
  await apiClient.post(`/api/property/${propertyId}`, body)
}

export async function deleteUserRole(userId: number, roleId: number): Promise<unknown> {
  const res = await apiClient.delete(`/api/user/${userId}/roles/${roleId}`)
  return res.data
}

export async function deleteUser(userId: number): Promise<void> {
  await apiClient.delete(`/api/user/${userId}/delete`)
}
